import { Control, Controller, useFormContext } from 'react-hook-form';
import { StyledOrderDeliverySiteWrapper, StyledOrderSiteWrapper } from '../utils/OrderStyles';
import { getErrorMessage } from '../../../utils/formHelper';

import { StyledLabel } from '@designSystem/InputWrapper/InputWrapperDesignStyles';
import {
  IOrderInformationsForm,
  IOrderInformationsOperaBusinessForm
} from '../utils/orderInterfaces';
import InputWrapper from '@designSystem/InputWrapper';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../app/hooks';
import { selectAdressInfos } from '@features/eligibility/eligibilitySlice';
import { TelephoneFill } from 'react-bootstrap-icons';
import Tooltip from '@designSystem/Tooltip';

/**
 * REGEX_ALPHANUM_DASH_SPACE
 * Description :
 * Cette expression régulière valide une chaîne de 3 à 100 caractères
 * composée de groupes alphanumériques (lettres ou chiffres), éventuellement
 * séparés par des espaces ou des tirets.
 *
 * Explications :
 *  - ^ et $ : permettent de matcher la totalité de la chaîne (du début à la fin).
 *  - (?=.{3,100}$) : impose une longueur de chaîne entre 3 et 100 caractères.
 *  - [0-9\p{L}]+ : requiert au moins un caractère alphanumérique (chiffres ou lettres Unicode).
 *  - ([-\s]+[0-9\p{L}]+)* : accepte plusieurs groupes précédés d'espaces ou de tirets.
 *  - /u : active le support Unicode.
 */
const REGEX_ALPHANUM_DASH_SPACE = /^(?=.{3,100}$)[0-9\p{L}]+([-\s]+[0-9\p{L}]+)*$/u;
const PHONE_REGEX = /^(?!00)(\+)?\d{10}$/;
const POSTAL_CODE_REGEX = /^[0-9]{1,5}$/;
const ADDRESS_REGEX = /^\d+\s+/;

const OrderInformationsDeliverySiteForm = ({
  orderInformationsFormValues,
  control
}: {
  orderInformationsFormValues: IOrderInformationsForm | IOrderInformationsOperaBusinessForm;
  control: Control<IOrderInformationsForm> | Control<IOrderInformationsOperaBusinessForm>;
}) => {
  const { register, formState, watch } = useFormContext();
  const { errors } = formState;
  const { t } = useTranslation();
  const addressInfos = useAppSelector(selectAdressInfos);
  const deliveryLastName = watch('deliveryLastName');
  const deliveryFirstName = watch('deliveryFirstName');

  const validateCombinedLength = () => {
    const totalLength =
      (deliveryLastName?.trim().length || 0) + (deliveryFirstName?.trim().length || 0);
    return (
      totalLength <= 23 ||
      t(
        'errorMessages.combinedLengthExceeded_23',
        `Le nombre maximum de 23 caractères pour les champs Nom et Prénom est dépassé.`
      )
    );
  };

  return (
    <StyledOrderSiteWrapper>
      <Controller
        name={'hasDeliveryAddress'}
        render={({ field: { onChange } }) => (
          <div className="no-gap">
            <StyledLabel className="section-label-question">
              {t(
                'features.order.orderInformations.hasDeliveryAddress',
                'Does the ONT box have to be delivered at a different address?'
              )}
            </StyledLabel>
            <div className="radio-container">
              <input
                onChange={() => onChange(true)}
                type="radio"
                id="hasDeliveryAddress_true"
                name="hasDeliveryAddress_true"
                value="true"
                checked={orderInformationsFormValues.hasDeliveryAddress}
              />
              <label htmlFor="existingOutlet_true">{t('app.yes', 'Yes')}</label>
            </div>
            <div className="radio-container">
              <input
                onChange={() => onChange(false)}
                type="radio"
                id="hasDeliveryAddress_false"
                name="hasDeliveryAddress"
                value="false"
                checked={!orderInformationsFormValues.hasDeliveryAddress}
              />
              <label htmlFor="hasDeliveryAddress_false">{t('app.no', 'No')}</label>
            </div>
          </div>
        )}
      />
      {orderInformationsFormValues.hasDeliveryAddress && (
        <StyledOrderDeliverySiteWrapper>
          <InputWrapper
            inputLabel={t('features.order.orderInformations.deliveryLastName', 'Last Name')}
            error={getErrorMessage('deliveryLastName', formState)}
          >
            <input
              type="text"
              id="deliveryLastName"
              data-testid="order-input-deliveryLastName"
              {...register('deliveryLastName', {
                setValueAs: (v) => v?.trimStart(),
                required: {
                  value: true,
                  message: t('errorMessages.valueMissing', `Missing value`)
                },
                validate: {
                  complexValidation: (value) =>
                    REGEX_ALPHANUM_DASH_SPACE.test(value) ||
                    t('errorMessages.invalid', 'Invalid format')
                }
              })}
              placeholder={t('features.order.orderInformations.deliveryLastName', `Last Name`)}
              value={orderInformationsFormValues.deliveryLastName}
              aria-label="deliveryLastName-input"
            />
          </InputWrapper>
          <InputWrapper
            inputLabel={t('features.order.orderInformations.deliveryFirstName', 'First Name')}
            error={getErrorMessage('deliveryFirstName', formState)}
          >
            <input
              type="text"
              id="firstName"
              data-testid="order-input-deliveryFirstName"
              {...register('deliveryFirstName', {
                setValueAs: (v) => v?.trimStart(),
                required: {
                  value: true,
                  message: t('errorMessages.valueMissing', `Missing value`)
                },
                validate: {
                  complexValidation: (value) =>
                    REGEX_ALPHANUM_DASH_SPACE.test(value) ||
                    t('errorMessages.invalid', 'Invalid format'),
                  combinedLength: validateCombinedLength
                }
              })}
              placeholder={t('features.order.orderInformations.deliveryFirstName', `First Name`)}
              value={orderInformationsFormValues.deliveryFirstName}
              aria-label="deliveryFirstName-input"
            />
          </InputWrapper>
          <div className={'full-width'}>
            <InputWrapper
              inputLabel={t('features.order.orderInformations.deliveryAddress', 'Address')}
              error={getErrorMessage('deliveryAddress', formState)}
            >
              <input
                type="text"
                id="deliveryAddress"
                data-testid="order-input-deliveryAddress"
                {...register('deliveryAddress', {
                  required: {
                    value: true,
                    message: t('errorMessages.valueMissing', `Missing value`)
                  },
                  validate: {
                    complexValidation: (value) =>
                      ADDRESS_REGEX.test(value) ||
                      t('errorMessages.invalidAddressNumber', 'Invalid format')
                  }
                })}
                placeholder={t('features.order.orderInformations.deliveryAddress', `Address`)}
                value={orderInformationsFormValues.deliveryAddress}
                aria-label="deliveryAddress-input"
              />
            </InputWrapper>
          </div>
          <InputWrapper
            inputLabel={t('features.order.orderInformations.deliveryCity', 'City')}
            error={getErrorMessage('deliveryCity', formState)}
          >
            <input
              type="text"
              id="deliveryCity"
              data-testid="order-input-deliveryCity"
              {...register('deliveryCity', {
                required: {
                  value: true,
                  message: t('errorMessages.valueMissing', `Missing value`)
                }
              })}
              placeholder={t('features.order.orderInformations.deliveryCity', `City`)}
              value={orderInformationsFormValues.deliveryCity}
              aria-label="deliveryCity-input"
            />
          </InputWrapper>
          <InputWrapper
            inputLabel={t('features.order.orderInformations.deliveryPostalCode', 'Postal Code')}
            error={getErrorMessage('deliveryPostalCode', formState)}
          >
            <input
              type="text"
              id="deliveryPostalCode"
              data-testid="order-input-deliveryPostalCode"
              {...register('deliveryPostalCode', {
                required: {
                  value: true,
                  message: t('errorMessages.valueMissing', `Missing value`)
                },
                pattern: {
                  value: POSTAL_CODE_REGEX,
                  message: t('errorMessages.invalid', `Invalid format`)
                }
              })}
              placeholder={t('features.order.orderInformations.deliveryPostalCode', `Postal Code`)}
              value={orderInformationsFormValues.deliveryPostalCode}
              aria-label="deliveryPostalCode-input"
            />
          </InputWrapper>
          <InputWrapper
            inputLabel={
              <>
                {t('features.order.orderInformations.deliveryPhone', 'Phone contact')}
                <Tooltip direction="bottom" max="175px">
                  {t(
                    'features.order.orderInformations.deliveryPhoneInfo',
                    'Fill out a phone number used for contacting during delivery'
                  )}
                </Tooltip>
              </>
            }
            error={getErrorMessage('deliveryPhone', formState)}
            icon={<TelephoneFill />}
          >
            <input
              type="text"
              id="deliveryPhone"
              data-testid="order-input-deliveryPhone"
              {...register('deliveryPhone', {
                required: {
                  value: true,
                  message: t('errorMessages.valueMissing', `Missing value`)
                },
                setValueAs: (v) => v?.replace(/[ ,-]/gm, ''),
                pattern: {
                  value: PHONE_REGEX,
                  message: t('errorMessages.invalid', `Invalid format`)
                },
                validate: (value) =>
                  PHONE_REGEX.test(value) || t('errorMessages.invalid', `Invalid format`)
              })}
              placeholder={t('features.order.orderInformations.deliveryPhone', `Phone contact`)}
              value={orderInformationsFormValues.deliveryPhone}
              aria-label="deliveryPhone-input"
            />
          </InputWrapper>
          <InputWrapper
            inputLabel={
              <>
                {t('features.order.orderInformations.deliverySite', 'Delivery Site')}
                <Tooltip direction="bottom" max="175px">
                  {t(
                    'features.order.orderInformations.deliverySiteInfo',
                    'Fill out the name of the delivery site, Ex: "Detached house"'
                  )}
                </Tooltip>
              </>
            }
            error={getErrorMessage('deliverySite', formState)}
          >
            <input
              type="text"
              id="deliverySite"
              data-testid="order-input-deliverySite"
              {...register('deliverySite', {
                required: {
                  value: true,
                  message: t('errorMessages.valueMissing', `Missing value`)
                },
                validate: {
                  complexValidation: (value) =>
                    REGEX_ALPHANUM_DASH_SPACE.test(value) ||
                    t('errorMessages.invalid', 'Invalid format')
                }
              })}
              placeholder={t('features.order.orderInformations.deliverySite', `Delivery Location`)}
              value={orderInformationsFormValues.deliverySite}
              aria-label="deliverySite-input"
            />
          </InputWrapper>
        </StyledOrderDeliverySiteWrapper>
      )}
    </StyledOrderSiteWrapper>
  );
};

export default OrderInformationsDeliverySiteForm;
